/**
 * Alpine Product Stores v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Page-specific Alpine stores and data for the product page only.
 * Loaded by snippets/scripts.liquid when template.name == 'product'.
 *
 * Stores:
 * - $store.productForm: Bridge for external ATC button / sticky bar
 * - $store.variant:     Current variant state + URL sync
 *
 * Data:
 * - productForm:        Product form controller
 * - productGallery:     Image/video carousel
 *
 * WHY IIFE (not ES Module):
 * Must register stores via alpine:init, which fires during Alpine's defer
 * execution. ES Modules execute after defer scripts and miss the event.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function() {
  'use strict';

  // alpine-stores.js runs first and sets window.Theme.Logger
  const Logger = window.Theme?.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console)
    })
  };

  const log = Logger.ns('product-stores');

  const setupProductStores = () => {
    if (typeof Alpine === 'undefined') return;

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.productForm - Bridge for external ATC button / sticky bar
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Lightweight reactive bridge so components OUTSIDE the productForm x-data
    // scope can read state and trigger addToCart. productForm syncs to this store
    // via $watch in init().
    //
    // Consumers: product-form-atc.liquid, product-sticky-bar.liquid
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.store('productForm', {
      ready: false,
      loading: false,
      error: null,
      selectedQty: 1,
      // Price data (synced by productForm on every state change)
      price: null,         // { perItem, total, discountPercent, hasDiscount, ... }
      basePrice: 0,
      compareAtPrice: 0,

      // Sticky bar visibility gates
      mainAtcVisible: true,  // true = #product-form-card is in viewport (set by IntersectionObserver in product-sticky-bar)
      delayPassed: false,    // true after 5s from page load (prevents bar flash on initial scroll)

      init() {
        setTimeout(() => { this.delayPassed = true; }, 5000);
      },

      addToCart() {
        // Function ref stored on window to avoid Alpine proxy issues with functions
        if (window._pfAddToCart) window._pfAddToCart();
      }
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // $store.variant - Current variant state + URL sync
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Central source of truth for the selected product variant.
    // Primary atc-form writes here; other components read via events or store.
    //
    // Two modes of operation:
    //   init(variant, selections)  → page load, silent (no URL push, no event)
    //   set(variant, selections)   → user action (pushes URL, dispatches event)
    //
    // Future image slider can watch: $store.variant.current?.featured_image
    // Bundle selectors (primary: false) don't interact with this store.
    // ─────────────────────────────────────────────────────────────────────────────

    const variantLog = Logger.ns('store.variant');

    Alpine.store('variant', {
      current: null,
      selections: {},
      _userDriven: false,

      // Silent init on page load — no URL push, no event dispatch.
      // Liquid SSR already used the correct variant (respects ?variant= param).
      init(variant, selections) {
        this.current = variant;
        this.selections = selections || {};
        variantLog.info('initialized', { id: variant?.id, selections });
      },

      // User-initiated variant change — notify listeners.
      set(variant, selections) {
        variantLog.warn('set() called', {
          variantId: variant?.id,
          hasImage: !!variant?.featured_image,
          imageId: variant?.featured_image?.id,
          selections
        });
        this._userDriven = true;
        this.current = variant;
        this.selections = selections || {};
        this._dispatch();
      },

      _dispatch() {
        variantLog.warn('_dispatch()', {
          currentId: this.current?.id,
          hasImage: !!this.current?.featured_image,
          imageId: this.current?.featured_image?.id
        });
        window.dispatchEvent(new CustomEvent('variant-selected', {
          detail: { variant: this.current }
        }));
      }
    });

    // ─────────────────────────────────────────────────────────────────────────────
    // Alpine.data('productForm') - Unified product form controller
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Single controller owning variant selection, quantity tiers, and add-to-cart.
    // All items are uniform — whether buying 1 or 5, each is a slot with its own
    // variant selection. addToCart() always sends { items: [...] }.
    //
    // Usage (via product-form.liquid):
    //   x-data="productForm(window._pfConfig)"
    //
    // Config (from Liquid):
    //   variants, options, optionValues, initialSelections,
    //   quantityTiers, maxQty, freeShippingThreshold, shippingCost, t
    // ─────────────────────────────────────────────────────────────────────────────

    const pfLog = Logger.ns('product-form');

    Alpine.data('productForm', (config) => ({
      // Product data (from Liquid)
      variants: config.variants,
      options: config.options,
      optionValues: config.optionValues,
      quantityTiers: config.quantityTiers || [],
      optionHasImages: config.optionHasImages || [],
      freeShippingThreshold: config.freeShippingThreshold || 5000,
      shippingCost: config.shippingCost || 299,

      // Items: array of { selections: {}, selectedVariant: null }
      // Pre-populated for all possible slots (0..maxQty-1)
      items: [],

      // State
      selectedQty: null,
      loading: false,
      error: null,
      ready: false,
      validationErrors: [],

      // Pricing detection
      get allSamePrice() {
        if (this.variants.length <= 1) return true;
        const first = this.variants[0].price;
        return this.variants.every(v => v.price === first);
      },

      get pricingOptionIndex() {
        if (this.variants.length <= 1) return null;
        if (this.options.length === 0) return null;

        // Priority 1: option where prices differ across values
        for (let i = 0; i < this.options.length; i++) {
          const optName = this.options[i];
          const prices = new Set();
          for (const val of this.optionValues[optName]) {
            const p = this._cheapestForValue(i, val);
            if (p !== null) prices.add(p);
          }
          if (prices.size > 1) return i;
        }

        // Priority 2: first option with images
        for (let i = 0; i < this.options.length; i++) {
          if (this.optionHasImages[i]) return i;
        }

        // Priority 3: last option
        return this.options.length - 1;
      },

      // Translations
      t: config.t || {},

      init() {
        const maxQty = config.maxQty || 1;
        const initial = config.initialSelections || {};

        // Create items: slot 0 gets initial selections, bundle slots start empty
        // (single-variant products: all slots get selections since no picker shown)
        const allGetInitial = config.variants.length <= 1;
        this.items = Array.from({ length: maxQty }, (_, idx) => ({
          selections: (idx === 0 || allGetInitial) ? { ...initial } : {},
          selectedVariant: null
        }));

        // Resolve variant for each slot (may be null if downstream options unset)
        for (let i = 0; i < this.items.length; i++) {
          this._resolveVariant(i);
        }

        // Sync primary slot to variant store (variant may be null for partial selection)
        Alpine.store('variant').init(this.items[0].selectedVariant, { ...this.items[0].selections });

        // Sync to $store.productForm so external ATC button / sticky bar can read state
        const pfStore = Alpine.store('productForm');
        // Store function ref on window (not on Alpine proxy) to avoid proxy issues
        window._pfAddToCart = () => this.addToCart();
        this.$watch('loading', v => { pfStore.loading = v; });
        this.$watch('error', v => { pfStore.error = v; });
        this.$watch('selectedQty', v => { pfStore.selectedQty = v; });

        this.$nextTick(() => {
          this.ready = true;
          pfStore.ready = true;
          if (this.selectedQty === null) {
            this.selectedQty = 1;
          }
          // Sync price + variant data to store for sticky bar
          this._syncPriceToStore();
          // Notify pricing snippet
          window.dispatchEvent(new CustomEvent('tier-selected', {
            detail: { qty: this.selectedQty, price: this.calcPrice(this.selectedQty) }
          }));
          pfLog.info('initialized', {
            items: this.items.length,
            selectedQty: this.selectedQty,
            variant: this.items[0].selectedVariant?.id
          });
        });
      },

      // ─── Variant Selection ────────────────────────────────────────

      // NOTE: All mutations use this.items[slot].x (direct proxy-path access)
      // to ensure Alpine v3 reactivity tracking. Do NOT capture const item = this.items[slot]
      // and mutate through the captured reference — it can break the proxy chain.

      selectOption(slot, optionName, value) {
        if (!this.items[slot]) return;

        // Clear validation error for this slot
        if (this.validationErrors.length > 0) {
          this.validationErrors = this.validationErrors.filter(s => s !== slot);
        }
        if (this.error) this.error = null;

        this.items[slot].selections[optionName] = value;

        // Backward fill: auto-select first available for unset upstream options
        const changedIdx = this.options.indexOf(optionName);
        for (let i = 0; i < changedIdx; i++) {
          const opt = this.options[i];
          if (this.items[slot].selections[opt]) continue;
          const firstAvailable = this.optionValues[opt]
            .find(v => this._isValueAvailable(slot, opt, v));
          this.items[slot].selections[opt] = firstAvailable || null;
        }

        this._resolveVariant(slot);

        // Any slot's option change navigates the gallery to matching image
        {
          const fullVariant = this.items[slot].selectedVariant;
          const partialMatch = !fullVariant ? this.variants.find(v =>
            v.featured_image && this.options.every((opt, i) => {
              const sel = this.items[slot].selections[opt];
              return !sel || v['option' + (i + 1)] === sel;
            })
          ) : null;
          const displayVariant = fullVariant || partialMatch || null;

          pfLog.warn(`selectOption slot ${slot}`, {
            selections: { ...this.items[slot].selections },
            fullVariant: fullVariant?.id || null,
            partialMatch: partialMatch?.id || null,
            displayVariant: displayVariant?.id || null,
            displayVariantImage: displayVariant?.featured_image?.id || null
          });

          Alpine.store('variant').set(displayVariant, { ...this.items[slot].selections });
        }

        // Notify pricing snippet of updated totals (skip during init)
        if (this.ready && this.selectedQty != null) {
          this._syncPriceToStore();
          window.dispatchEvent(new CustomEvent('tier-selected', {
            detail: { qty: this.selectedQty, price: this.calcPrice(this.selectedQty) }
          }));
        }
      },

      _isValueAvailable(slot, optionName, value) {
        const optIdx = this.options.indexOf(optionName);
        return this.variants.some(v => {
          if (v['option' + (optIdx + 1)] !== value) return false;
          // Only check upstream options (hierarchical: parent constrains child, not reverse)
          for (let i = 0; i < optIdx; i++) {
            const sel = this.items[slot].selections[this.options[i]];
            if (sel && v['option' + (i + 1)] !== sel) return false;
          }
          return v.available;
        });
      },

      isValueSoldOut(slot, optionName, value) {
        if (this._isValueAvailable(slot, optionName, value)) return false;
        const optIdx = this.options.indexOf(optionName);
        return this.variants.some(v => {
          if (v['option' + (optIdx + 1)] !== value) return false;
          for (let i = 0; i < optIdx; i++) {
            const sel = this.items[slot].selections[this.options[i]];
            if (sel && v['option' + (i + 1)] !== sel) return false;
          }
          return true;
        });
      },

      _resolveVariant(slot) {
        this.items[slot].selectedVariant = this.variants.find(v => {
          return this.options.every((opt, i) => {
            return v['option' + (i + 1)] === this.items[slot].selections[opt];
          });
        }) || null;
      },

      // ─── Tier Selection ───────────────────────────────────────────

      selectTier(qty) {
        this.selectedQty = qty;
        // Pre-populate bundle slots (1+): first option from slot 0, rest unset
        if (this.options.length > 0) {
          const firstOpt = this.options[0];
          const firstVal = this.items[0].selections[firstOpt] || null;
          for (let i = 1; i < qty; i++) {
            if (!this.items[i]) continue;
            const hasSelections = this.options.some(opt => this.items[i].selections[opt]);
            if (!hasSelections) {
              this.items[i].selections[firstOpt] = firstVal;
            }
            // Resolve variant for pre-populated slots (covers single-option products)
            this._resolveVariant(i);
          }
        }
        this._syncPriceToStore();
        window.dispatchEvent(new CustomEvent('tier-selected', {
          detail: { qty, price: this.calcPrice(qty) }
        }));
      },

      isSelected(qty) { return this.selectedQty === qty; },

      hasSlotError(slot) { return this.validationErrors.includes(slot); },

      validate() {
        const errors = [];
        const soldOutSlots = [];
        for (let i = 0; i < this.selectedQty; i++) {
          if (!this.items[i]?.selectedVariant) {
            errors.push(i);
          } else if (!this.items[i].selectedVariant.available) {
            soldOutSlots.push(i);
          }
        }
        this.validationErrors = [...errors, ...soldOutSlots];
        const allErrors = this.validationErrors;
        if (allErrors.length > 0) {
          if (errors.length > 0) {
            // Find first missing option name for the error message
            const slot = errors[0];
            const missingOpt = this.options.find(opt => !this.items[slot].selections[opt]);
            if (missingOpt && this.t.selectOptionError) {
              this.error = this.t.selectOptionError.replace('OPTION', missingOpt.toUpperCase());
            } else {
              this.error = this.t.error || 'Please select all options';
            }
          } else {
            this.error = this.t.soldOut || 'Sold out';
          }
          this.$nextTick(() => {
            const el = this.$el.querySelector('[data-slot="' + allErrors[0] + '"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
        return allErrors.length === 0;
      },

      // ─── Pricing ──────────────────────────────────────────────────

      _cheapestForValue(optIdx, value) {
        let cheapest = null;
        for (const v of this.variants) {
          if (v['option' + (optIdx + 1)] !== value) continue;
          if (cheapest === null || v.price < cheapest) cheapest = v.price;
        }
        return cheapest;
      },

      pillPrice(slot, optionName, value) {
        const optIdx = this.options.indexOf(optionName);
        if (optIdx !== this.pricingOptionIndex) return null;

        const selections = this.items[slot]?.selections || {};

        // If cross-options are filled, use exact variant match
        const othersFilled = this.options.every((opt, i) =>
          i === optIdx || selections[opt]
        );

        let basePrice = null;
        if (othersFilled) {
          const variant = this.variants.find(v => {
            return this.options.every((opt, i) => {
              if (i === optIdx) return v['option' + (i + 1)] === value;
              return v['option' + (i + 1)] === selections[opt];
            });
          });
          if (variant) basePrice = variant.price;
        }

        // Fallback: cheapest variant for this value
        if (basePrice === null) basePrice = this._cheapestForValue(optIdx, value);
        if (basePrice === null) return null;

        return Alpine.store('pricing').calculate(basePrice, this.selectedQty || 1, {
          discountType: 'quantity_tiers',
          quantityTiers: this.quantityTiers
        }).perItem;
      },

      _slotPrice(slot) {
        if (this.items[slot]?.selectedVariant) {
          return this.items[slot].selectedVariant.price;
        }
        // Unresolved: cheapest variant matching partial selections
        const selections = this.items[slot]?.selections || {};
        let cheapest = null;
        for (const v of this.variants) {
          const matches = this.options.every((opt, i) => {
            const sel = selections[opt];
            return !sel || v['option' + (i + 1)] === sel;
          });
          if (matches && (cheapest === null || v.price < cheapest)) {
            cheapest = v.price;
          }
        }
        return cheapest || this.variants[0]?.price || 0;
      },

      get basePrice() {
        return this._slotPrice(0);
      },

      get compareAtPrice() {
        return this.items[0]?.selectedVariant?.compare_at_price || 0;
      },

      get available() {
        return this.items[0]?.selectedVariant?.available || false;
      },

      calcPrice(qty) {
        const pricing = Alpine.store('pricing');
        const opts = { discountType: 'quantity_tiers', quantityTiers: this.quantityTiers };

        if (qty <= 1) {
          return pricing.calculate(this.basePrice, 1, opts);
        }

        // Bundle: sum each slot's price with tier discount applied
        let total = 0, totalOriginal = 0;
        for (let i = 0; i < qty; i++) {
          const price = this._slotPrice(i);
          const r = pricing.calculate(price, qty, opts);
          total += r.perItem;
          totalOriginal += price;
        }

        const totalSaved = totalOriginal - total;
        return {
          perItem: Math.round(total / qty),
          perItemOriginal: Math.round(totalOriginal / qty),
          perItemSaved: Math.round(totalSaved / qty),
          total, totalOriginal, totalSaved,
          discountPercent: totalOriginal > 0 ? Math.round((totalSaved / totalOriginal) * 100) : 0,
          hasDiscount: totalSaved > 0,
          label: null
        };
      },

      hasFreeShipping(total) { return total >= this.freeShippingThreshold; },

      // Push price + variant data to $store.productForm for sticky bar
      _syncPriceToStore() {
        const pfStore = Alpine.store('productForm');
        pfStore.price = this.calcPrice(this.selectedQty || 1);
        pfStore.basePrice = this.basePrice;
        pfStore.compareAtPrice = this.compareAtPrice;
      },

      // ─── Add to Cart (unified, always multi-item) ────────────────

      async addToCart() {
        if (this.loading) return;
        this.error = null;
        if (!this.validate()) return;
        if (!this.selectedQty) return;
        this.loading = true;

        try {
          const pricingProps = Alpine.store('pricing').getCartProperties('quantity_tiers');
          const items = [];

          for (let i = 0; i < this.selectedQty; i++) {
            const variant = this.items[i]?.selectedVariant;
            if (!variant) {
              this.error = this.t.error || 'No variant selected';
              this.loading = false;
              return;
            }
            items.push({
              id: variant.id,
              quantity: 1,
              properties: { ...pricingProps }
            });
          }

          pfLog.info('addToCart', { qty: this.selectedQty, items: items.map(i => i.id) });
          await Alpine.store('cart').add({ items });
          window.location.href = '/cart';
          return;
        } catch (err) {
          this.error = err.message || this.t.error;
          pfLog.error('addToCart failed', err);
        } finally {
          this.loading = false;
        }
      }
    }));

    // ─────────────────────────────────────────────────────────────────────────────
    // Alpine.data('productGallery') - Product image/video carousel
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Carousel with thumbnail navigation and variant-to-image sync.
    // Variant store drives gallery (variant change → auto-navigate to image).
    // Gallery does NOT drive variant (browsing photos doesn't change variant).
    //
    // Usage (via product-gallery.liquid):
    //   x-data="productGallery(window._pg['uid'])"
    //
    // Config (from Liquid):
    //   slideCount, initialIndex, mediaIdToIndex
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.data('productGallery', (config) => ({
      slideCount: config.slideCount,
      currentIndex: config.initialIndex || 0,
      mediaIdToIndex: config.mediaIdToIndex || {},

      // Desktop thumbnail arrow nav (vertical, translateY-based)
      thumbOffset: 0,
      thumbMaxOffset: 0,
      galleryHeight: 0,

      // Mobile thumbnail arrow visibility (native scroll)
      canScrollMLeft: false,
      canScrollMRight: false,

      _scrollTimer: null,

      init() {
        const galLog = Logger.ns('gallery');
        galLog.warn('gallery init', {
          slideCount: this.slideCount,
          mediaIdToIndex: this.mediaIdToIndex
        });

        // Only navigate gallery on user-driven selections, not the initial
        // $store.variant.init() assignment. SSR already renders the correct slide.
        this.$watch('$store.variant.current', (variant) => {
          if (!this.$store.variant._userDriven) return;
          galLog.warn('variant watcher fired', {
            variantId: variant?.id,
            hasImage: !!variant?.featured_image,
            imageId: variant?.featured_image?.id,
            mediaIdToIndex: this.mediaIdToIndex,
            mappedIndex: variant?.featured_image ? this.mediaIdToIndex[variant.featured_image.id] : 'n/a'
          });
          if (!variant?.featured_image) return;
          this.goToVariantImage(variant.featured_image.id);
        });

        this.$nextTick(() => {
          // Desktop: carousel height drives thumbnail container
          const c = this.$refs.carousel;
          if (c) {
            new ResizeObserver(([e]) => {
              this.galleryHeight = e.contentRect.height;
            }).observe(c);
          }

          // Desktop: recalc on viewport resize (arrow show/hide changes size)
          const tv = this.$refs.thumbViewport;
          if (tv) new ResizeObserver(() => this._syncThumbNav()).observe(tv);

          // Main carousel: detect swipe/drag via scroll events
          const track = this.$refs.track;
          if (track) {
            if (this.currentIndex > 0) {
              const slides = track.querySelectorAll('.pg-slide');
              if (slides[this.currentIndex]) {
                track.scrollTo({ left: slides[this.currentIndex].offsetLeft, behavior: 'instant' });
              }
            }
            track.addEventListener('scroll', () => {
              clearTimeout(this._scrollTimer);
              this._scrollTimer = setTimeout(() => {
                const w = track.clientWidth;
                if (!w) return;
                const idx = Math.round(track.scrollLeft / w);
                if (idx !== this.currentIndex && idx >= 0 && idx < this.slideCount) {
                  this.currentIndex = idx;
                  this._scrollThumbIntoView(idx);
                }
              }, 60);
            }, { passive: true });
          }

          // Mobile thumbnails: native scroll arrow visibility
          const ms = this.$refs.mThumbStrip;
          if (ms) {
            const updateM = () => this._updateMobileArrows();
            updateM();
            ms.addEventListener('scroll', updateM, { passive: true });
            new ResizeObserver(updateM).observe(ms);
            // Scroll active thumb into view on init
            if (this.currentIndex > 0) {
              const thumb = ms.querySelector(`[data-thumb="${this.currentIndex}"]`);
              if (thumb) ms.scrollTo({ left: thumb.offsetLeft, behavior: 'instant' });
            }
          }
        });
      },

      // ── Desktop thumbnails (vertical, translateY) ───────────────────────
      _syncThumbNav() {
        const viewport = this.$refs.thumbViewport;
        const strip = this.$refs.thumbStrip;
        if (!viewport || !strip) return;
        this.thumbMaxOffset = Math.max(0, strip.scrollHeight - viewport.clientHeight);
        if (this.thumbOffset > this.thumbMaxOffset) this.thumbOffset = this.thumbMaxOffset;
      },

      scrollThumbsUp() {
        const step = this.$refs.thumbViewport?.clientHeight || 300;
        this.thumbOffset = Math.max(0, this.thumbOffset - step);
      },

      scrollThumbsDown() {
        const step = this.$refs.thumbViewport?.clientHeight || 300;
        this.thumbOffset = Math.min(this.thumbMaxOffset, this.thumbOffset + step);
      },

      // ── Mobile thumbnails (horizontal, scroll-snap grid) ────────────────
      _updateMobileArrows() {
        const el = this.$refs.mThumbStrip;
        if (!el) return;
        this.canScrollMLeft = el.scrollLeft > 1;
        this.canScrollMRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
      },

      _mThumbMetrics() {
        const el = this.$refs.mThumbStrip;
        const items = el?.querySelectorAll('.pg-mt-thumb');
        if (!el || !items?.length) return null;
        const gap = parseFloat(getComputedStyle(el).gap) || 0;
        const step = items[0].offsetWidth + gap;
        const visible = Math.round(el.clientWidth / step) || 1;
        return { items, step, visible };
      },

      scrollMobileLeft() {
        const el = this.$refs.mThumbStrip;
        if (!el) return;
        const m = this._mThumbMetrics();
        if (!m) return;
        const current = Math.round(el.scrollLeft / m.step);
        const target = Math.max(0, current - m.visible);
        el.scrollTo({ left: m.items[target].offsetLeft, behavior: 'smooth' });
      },

      scrollMobileRight() {
        const el = this.$refs.mThumbStrip;
        if (!el) return;
        const m = this._mThumbMetrics();
        if (!m) return;
        const current = Math.round(el.scrollLeft / m.step);
        let target = current + m.visible;
        if (target >= m.items.length) target = m.items.length - 1;
        el.scrollTo({ left: m.items[target].offsetLeft, behavior: 'smooth' });
      },

      // ── Shared ───────────────────────────────────────────────────────────
      _scrollThumbIntoView(index) {
        // Desktop (vertical)
        const strip = this.$refs.thumbStrip;
        const viewport = this.$refs.thumbViewport;
        if (strip && viewport && viewport.clientHeight) {
          const thumb = strip.children[index];
          if (thumb) {
            const top = thumb.offsetTop;
            const bot = top + thumb.offsetHeight;
            if (top < this.thumbOffset) this.thumbOffset = top;
            else if (bot > this.thumbOffset + viewport.clientHeight)
              this.thumbOffset = bot - viewport.clientHeight;
          }
        }
        // Mobile (horizontal, native scroll)
        const ms = this.$refs.mThumbStrip;
        if (ms) {
          const thumb = ms.querySelector(`[data-thumb="${index}"]`);
          if (thumb) {
            const left = thumb.offsetLeft;
            const right = left + thumb.offsetWidth;
            if (left < ms.scrollLeft) {
              ms.scrollTo({ left, behavior: 'smooth' });
            } else if (right > ms.scrollLeft + ms.clientWidth) {
              ms.scrollTo({ left: right - ms.clientWidth, behavior: 'smooth' });
            }
          }
        }
      },

      goTo(index) {
        if (index < 0 || index >= this.slideCount || index === this.currentIndex) return;
        this.currentIndex = index;
        const track = this.$refs.track;
        if (track) {
          const slides = track.querySelectorAll('.pg-slide');
          if (slides[index]) {
            track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
          }
        }
        this.$nextTick(() => this._scrollThumbIntoView(index));
      },

      next()     { this.goTo((this.currentIndex + 1) % this.slideCount); },
      previous() { this.goTo((this.currentIndex - 1 + this.slideCount) % this.slideCount); },

      goToVariantImage(imageId) {
        const galLog = Logger.ns('gallery');
        const idx = this.mediaIdToIndex[imageId];
        galLog.warn('goToVariantImage', { imageId, mappedIndex: idx, currentIndex: this.currentIndex });
        if (idx !== undefined) this.goTo(idx);
      },

      isActive(i) { return this.currentIndex === i; }
    }));

    log.info('Alpine product stores registered');
  };

  document.addEventListener('alpine:init', setupProductStores);

  log.info('Alpine product stores v1.0.0 loaded');
})();
